/* Theme presentation and customer-session coupon application only. */
(() => {
  'use strict';
  if (window.SainlogicOffers) {
    window.SainlogicOffers.init();
    return;
  }

  const configElement = document.getElementById('sainlogic-offer-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch (_) { return; }
  // Liquid's translation filter HTML-escapes text even when it is then JSON-encoded.
  // Decode only text entities; never interpret translated strings as HTML.
  const decodeText = (value) => String(value ?? '').replace(/&(?:#(\d+)|#x([\da-f]+)|(amp|quot|apos|lt|gt|nbsp));/gi, (entity, decimal, hexadecimal, named) => {
    if (named) return {amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: '\u00a0'}[named.toLowerCase()];
    const codepoint = Number.parseInt(decimal || hexadecimal, decimal ? 10 : 16);
    return codepoint > 0 && codepoint <= 0x10ffff && !(codepoint >= 0xd800 && codepoint <= 0xdfff) ? String.fromCodePoint(codepoint) : entity;
  });
  const strings = Object.fromEntries(Object.entries(config.strings || {}).map(([key, value]) => [key, decodeText(value)]));
  const root = window.Shopify?.routes?.root || '/';
  const verifiedRules = Object.freeze({
    SA40OFF: ['9787274166568', '9787274854696', '9787271905576', '9871952576808', '10129835852072'],
    SA43OFF: ['9646427865384'],
    SA45OFF: ['9686314189096'],
    SA33OFF: ['9693054468392'],
    SA55OFF: ['9686318121256'],
    SA76OFF: ['9646331167016'],
    SA50OFF: ['9787272036648'],
    SA50CUT: ['10359067115816', '10355168969000'],
    SA70CUT: ['10356269744424']
  });
  let applyQueue = Promise.resolve();
  let pendingApplications = 0;
  const initialized = new WeakSet();
  const heldPurchases = new Map();
  const purchaseSelector = [
    'form[action*="/cart/add"] button[type="submit"]',
    'form[action*="/cart/add"] input[type="submit"]',
    '.sticky-cta .button',
    '.cart__checkout-button',
    '.additional-checkout-buttons',
    '#cart-notification-form'
  ].join(',');

  function holdPurchases() {
    if (!pendingApplications) return;
    document.querySelectorAll(purchaseSelector).forEach((element) => {
      if (heldPurchases.has(element)) return;
      // Leave disabled/aria-disabled under Ignite's control, including sold-out variants.
      heldPurchases.set(element, element.hasAttribute('inert'));
      element.setAttribute('inert', '');
    });
  }

  const purchaseObserver = typeof MutationObserver === 'function' ? new MutationObserver(holdPurchases) : null;

  function releasePurchases() {
    if (pendingApplications) return;
    purchaseObserver?.disconnect();
    heldPurchases.forEach((wasInert, element) => {
      if (!wasInert) element.removeAttribute('inert');
    });
    heldPurchases.clear();
  }

  document.addEventListener('submit', (event) => {
    if (!pendingApplications || event.target?.tagName !== 'FORM') return;
    const action = event.submitter?.getAttribute('formaction') || event.target.getAttribute('action') || '';
    let pathname;
    try { pathname = new URL(action, location.href).pathname; } catch (_) { return; }
    if (!/\/(?:cart(?:\/add(?:\.js)?)?|checkout)\/?$/.test(pathname)) return;
    // Block rather than replay: the form or selected variant may change while waiting.
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  const safeCode = (value) => typeof value === 'string' && value.length > 0 && value.length <= 256 && !/[\u0000-\u001f\u007f,]/.test(value);
  const canonicalCode = (value) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return verifiedRules[trimmed.toUpperCase()] ? trimmed.toUpperCase() : trimmed;
  };
  const matchesProduct = (code, productId) => verifiedRules[code]?.includes(String(productId)) === true;
  const cartHasEligibleItem = (cart, code) => (cart.items || []).some((item) => matchesProduct(code, item.product_id));

  async function requestCart(path, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(root + path, {
        credentials: 'same-origin',
        headers: {Accept: 'application/json', ...(options.body ? {'Content-Type': 'application/json'} : {})},
        ...options,
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { window.clearTimeout(timeout); }
  }

  function discountApplications(cart) {
    return [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application))
    ].filter(Boolean);
  }

  function existingCodes(cart) {
    const fromCodes = (cart.discount_codes || []).map((entry) => typeof entry === 'string' ? entry : entry.code);
    const fromAllocations = discountApplications(cart)
      .filter((application) => application.type === 'discount_code')
      .map((application) => application.title);
    return [...fromCodes, ...fromAllocations].filter(safeCode);
  }

  function wasApplied(cart, code) {
    return (cart.discount_codes || []).some((entry) => typeof entry === 'object' && String(entry.code).toUpperCase() === code.toUpperCase() && entry.applicable === true) ||
      discountApplications(cart).some((application) => application.type === 'discount_code' && String(application.title).toUpperCase() === code.toUpperCase() && Number(application.total_allocated_amount) > 0);
  }

  function statusFor(cart, code) {
    if (wasApplied(cart, code)) return 'applied';
    const retained = existingCodes(cart).some((entry) => entry.toUpperCase() === code.toUpperCase());
    if (verifiedRules[code] && retained && !cartHasEligibleItem(cart, code)) return 'saved';
    return 'not_applied';
  }

  function setStatus(offer, key) {
    const status = offer?.querySelector('[data-offer-status]');
    if (!status) return;
    status.textContent = strings[key] || '';
    status.hidden = !status.textContent;
    status.dataset.error = String(['failed', 'not_applied', 'copy_failed'].includes(key));
  }

  function cartSections() {
    const drawer = document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-items');
    const ids = new Set();
    if (typeof drawer?.getSectionsToRender === 'function') {
      drawer.init();
      drawer.getSectionsToRender().forEach((section) => ids.add(section.id));
    }
    if (typeof cartItems?.getSectionsToRender === 'function') {
      cartItems.getSectionsToRender().forEach((section) => ids.add(section.section));
    }
    return {drawer, cartItems, ids: [...ids]};
  }

  function refreshCartUI(cart, components, reloadUrl) {
    const {drawer, cartItems, ids} = components;
    const reload = () => reloadUrl ? window.location.replace(reloadUrl) : window.location.reload();
    try {
      // Use the current Ignite component contract and bundled section HTML.
      // Rendering with preventOpen=true keeps claiming a code from opening the drawer.
      if (ids.some((id) => typeof cart.sections?.[id] !== 'string')) throw new Error('Missing cart section');
      if (typeof drawer?.renderContents === 'function') {
        drawer.renderContents(cart, true);
        drawer.classList.toggle('is-empty', cart.item_count === 0);
      }
      if (typeof cartItems?.renderContents === 'function') cartItems.renderContents(cart);
      // The notification shows a previous add-to-cart event and an old line key.
      // Close it rather than showing its previous price after a coupon changes that key.
      const notification = document.querySelector('cart-notification');
      if (notification?.notification?.classList.contains('active')) notification.close();
      if ((drawer && typeof drawer.renderContents !== 'function' || cartItems && typeof cartItems.renderContents !== 'function') && cart.item_count > 0) {
        reload();
      }
    } catch (_) {
      // A successfully applied code must not leave visible stale cart totals.
      reload();
    }
  }

  function applyCode(code, allowUnlisted = false, onAccepted) {
    pendingApplications += 1;
    if (pendingApplications === 1) {
      holdPurchases();
      if (document.body) purchaseObserver?.observe(document.body, {childList: true, subtree: true});
    }
    const operation = async () => {
      if (!safeCode(code) || (!verifiedRules[code] && !allowUnlisted)) throw new Error('Unsupported code');
      const currentCart = await requestCart('cart.js');
      // Preserve customer-entered codes; Shopify decides eligibility and combinations.
      const codes = [...new Map([...existingCodes(currentCart), code].map((entry) => [entry.toUpperCase(), entry])).values()];
      const components = cartSections();
      const payload = {discount: codes.join(',')};
      if (components.ids.length) {
        payload.sections = components.ids;
        payload.sections_url = location.pathname;
      }
      const cart = await requestCart('cart/update.js', {method: 'POST', body: JSON.stringify(payload)});
      let reloadUrl;
      try { reloadUrl = onAccepted?.(); } catch (_) { /* URL cleanup cannot cancel an accepted cart update. */ }
      refreshCartUI(cart, components, reloadUrl);
      document.dispatchEvent(new CustomEvent('sainlogic:offer-applied', {detail: {cart, code}}));
      return cart;
    };
    const result = applyQueue.then(operation, operation);
    applyQueue = result.catch(() => {});
    return result.finally(() => {
      pendingApplications -= 1;
      releasePurchases();
    });
  }

  async function copyCode(code) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
        return true;
      }
    } catch (_) { /* Clipboard permissions must not block coupon application. */ }
    const input = document.createElement('textarea');
    input.value = code;
    input.setAttribute('readonly', '');
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
    document.body.append(input);
    input.select();
    let copied = false;
    try { copied = document.execCommand('copy'); } catch (_) { /* Show the visible code for manual entry. */ }
    input.remove();
    return copied;
  }

  function updateVariant(offer) {
    if (offer.classList.contains('sainlogic-offer--compact')) return;
    const form = offer.dataset.formId ? document.getElementById(offer.dataset.formId) : offer.closest('main')?.querySelector('form[action*="/cart/add"]');
    const variantId = form?.querySelector('[name="id"]')?.value;
    if (!variantId) return;
    let variants;
    try { variants = JSON.parse(offer.querySelector('[data-offer-variants]').textContent); } catch (_) { return; }
    const variant = variants.find((entry) => String(entry.id) === String(variantId));
    const price = offer.querySelector('[data-offer-price]');
    if (!variant || !price) return;
    price.hidden = typeof variant.money !== 'string';
    price.textContent = price.hidden ? '' : decodeText(variant.money.replace(/<[^>]*>/g, ''));
  }

  function init() {
    document.querySelectorAll('[data-sainlogic-offer]').forEach((offer) => {
      if (initialized.has(offer)) return;
      const code = canonicalCode(offer.dataset.code);
      if (!matchesProduct(code, offer.dataset.productId)) return;
      initialized.add(offer);
      offer.querySelectorAll('[data-offer-apply], [data-offer-copy]').forEach((button) => { button.hidden = false; });
      offer.querySelector('[data-offer-apply]')?.addEventListener('click', async (event) => {
        event.preventDefault();
        const link = event.currentTarget;
        if (link.getAttribute('aria-disabled') === 'true') return;
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('aria-busy', 'true');
        setStatus(offer, 'applying');
        try {
          const cart = await applyCode(code);
          setStatus(offer, statusFor(cart, code));
        } catch (_) { setStatus(offer, 'failed'); }
        finally {
          link.removeAttribute('aria-disabled');
          link.removeAttribute('aria-busy');
        }
      });
      offer.querySelector('[data-offer-copy]')?.addEventListener('click', async () => setStatus(offer, await copyCode(code) ? 'copied' : 'copy_failed'));
      updateVariant(offer);
    });
  }

  async function handleEmailCode() {
    if (config.enable_email_codes !== true) return;
    const code = canonicalCode(new URLSearchParams(location.search).get('willDiscountCode'));
    if (!safeCode(code)) return;
    const knownCode = Boolean(verifiedRules[code]);
    // Unrecognized personalized campaign codes are left untouched by default,
    // rather than labelled invalid or copied onto unrelated product links.
    if (!knownCode && config.allow_unlisted_email_codes !== true) return;
    try {
      const pageProductId = String(config.page_product_id || '0');
      if (knownCode && pageProductId !== '0' && !matchesProduct(code, pageProductId)) return;
      // A code may be saved before the shopper adds an item. Its actual
      // applicability, including personalized campaign scope, belongs to Shopify.
      // Never attach the incoming code to product links or client storage.
      const updatedCart = await applyCode(code, !knownCode, () => {
        // Consume only the coupon parameter after Shopify accepts the request.
        // This prevents an email link from reapplying on the refresh fallback.
        const cleanUrl = new URL(location.href);
        cleanUrl.searchParams.delete('willDiscountCode');
        try { window.history.replaceState(window.history.state, '', cleanUrl.toString()); } catch (_) { /* Same-origin navigation remains the refresh fallback. */ }
        return cleanUrl.toString();
      });
      document.querySelectorAll('[data-sainlogic-offer]').forEach((offer) => {
        if (offer.dataset.code === code) setStatus(offer, statusFor(updatedCart, code));
      });
    } catch (_) { /* Keep campaign links and checkout usable on network failure. */ }
  }

  const updateVariants = () => window.setTimeout(() => {
    init();
    document.querySelectorAll('[data-sainlogic-offer]').forEach(updateVariant);
  }, 0);
  document.addEventListener('change', updateVariants);
  document.addEventListener('variant:change', updateVariants);
  document.addEventListener('variant-change', updateVariants);
  document.addEventListener('product:variant-change', updateVariants);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('quick-view-open', init);
  window.SainlogicOffers = Object.freeze({init});
  init();
  handleEmailCode();
})();
